<template>
	<v-row v-if="!showPrintPreview" class="full-height">
		<v-col cols="12" class="flex-none">
			<v-alert
				v-if="$store.getters['ezeepHealth/ezeepHasIssues']"
				:type="$store.getters['ezeepHealth/ezeepStatus'] === 'error' ? 'error' : 'warning'"
				text
				dense
				dismissible
				class="mb-2"
			>
				<strong>Printer Issue:</strong> {{ $store.getters['ezeepHealth/ezeepStatusMessage'] }}.
				Auto-print may not work.
				<router-link v-if="canViewPrinterSettings" to="/settings/printers" class="ml-1">View Printers</router-link>
			</v-alert>
			<div class="po-actions mb-6">
				<p v-if="lockConfirmation" class="pr-2 d-flex flex-column justify-center">
					{{ isLocked ? "PO Locked!" : "PO Unlocked!" }}
				</p>
				<v-tooltip bottom :disabled="purchaseOrder.received === 0" v-if="!poData.isCancelled">
					<template v-slot:activator="{ on }">
						<div v-on="on" style="height: 36px; min-width: 64px; margin-right: 0.8rem">
							<v-btn
								depressed
								outlined
								@click="handleCancelPO"
								:disabled="!poData.orderNumber || purchaseOrder.received > 0 || !canCancelPO()"
							>
								Cancel PO
							</v-btn>
						</div>
					</template>
					<span>PO cannot be cancelled because inventory has been received in</span>
				</v-tooltip>
				<v-btn v-else depressed outlined :disabled="!poData.orderNumber" @click="handleRestorePO">
					Restore PO
				</v-btn>

				<v-btn depressed outlined @click="$router.back()">Close</v-btn>
				<v-btn
					v-if="poData && poData.purchaseOrderId"
					depressed
					outlined
					@click="save"
					:disabled="!canUpdatePO()"
					>Save</v-btn
				>
			</div>
			<div class="po-form">
				<template v-if="poData.vendorId == null || editVendor">
					<v-row>
						<v-col class="po-form-header">
							<div class="form-input">
								<v-combobox
									prepend-inner-icon="mdi-magnify"
									dense
									outlined
									clearable
									placeholder="Search Vendor"
									hide-details="auto"
									v-model="vendorSearch"
									:items="vendorResults"
									item-text="companyName"
									item-value="text"
									@change="setVendor()"
									@keyup="searchVendors($event.srcElement.value)"
								></v-combobox>
							</div>
							<span class="ml-2">
								or <router-link :to="`/relationships/vendor`">create a new vendor</router-link>
							</span>
						</v-col>
					</v-row>

					<v-row v-if="poData.vendorId == null">
						<v-col cols="3">
							<div class="form-input">
								<label>Vendor Contact</label>
								<v-select
									v-model="poData.vendorContactId"
									:items="contacts"
									item-text="firstName"
									item-value="vendorContactId"
									dense
									outlined
									hide-details="auto"
								></v-select>
							</div>
						</v-col>

						<v-col cols="3">
							<div class="form-input">
								<label>Vendor #</label>
								<v-combobox
									prepend-inner-icon="mdi-magnify"
									dense
									outlined
									clearable
									hide-details="auto"
									v-model="vendorSearch"
									:items="vendorResults"
									item-text="vendorId"
									item-value="text"
									@change="setVendor()"
									@keyup="searchVendors($event.srcElement.value)"
									:error="
										errors &&
										errors.vendorId !== null &&
										errors.vendorId !== '' &&
										errors.vendorId !== undefined
									"
								>
									<template v-slot:item="{ item, attrs, on }">
										<v-list-item v-on="on" v-bind="attrs">
											<v-list-item-content>
												<v-list-item-title>
													<v-row no-gutters align="center">
														{{ `${item.vendorId} - ${item.companyName}` }}
													</v-row>
												</v-list-item-title>
											</v-list-item-content>
										</v-list-item>
									</template>
								</v-combobox>
								<p v-if="errors.vendorId" class="validation-error">
									{{ errors.vendorId }}
								</p>
							</div>
						</v-col>

						<v-col cols="3">
							<div class="form-input">
								<label>Purchase Order #</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="poData.orderNumber"
									placeholder="generated after save"
									disabled
									><template v-if="poData.orderNumber" v-slot:append-outer>
										<v-tooltip bottom>
											<template v-slot:activator="{ on }">
												<router-link
													:to="{
														path: `/receiving/receiving-entry/${poData.purchaseOrderId}`,
													}"
													><v-icon class="to-order" v-on="on"> mdi-hand-coin </v-icon></router-link
												>
											</template>
											Go to Receiving
										</v-tooltip>
									</template></v-text-field
								>
							</div>
						</v-col>
					</v-row>
				</template>

				<template v-if="poData.vendorId !== null">
					<v-row>
						<v-col class="po-form-header">
							<h1 class="po-form-title">
								{{ poData.companyName }}
							</h1>

							<StatusBadge v-if="poData.orderNumber" :value="purchaseOrder.orderStatus" />

							<v-tooltip bottom>
								<template v-slot:activator="{ on }">
									<div v-on="on" style="height: 36px; margin-left: 0.8rem">
										<v-hover v-if="isLocked">
											<template v-slot:default="{ hover }">
												<v-btn depressed @click="handleLock" icon :disabled="!canUpdatePO()">
													<v-icon v-if="!hover"> mdi-lock </v-icon>
													<v-icon v-else> mdi-lock-open-variant </v-icon>
												</v-btn>
											</template>
										</v-hover>
										<v-hover v-else>
											<template v-slot:default="{ hover }">
												<v-btn depressed @click="handleLock" icon :disabled="!canUpdatePO()">
													<v-icon v-if="hover"> mdi-lock </v-icon>
													<v-icon v-else> mdi-lock-open-variant </v-icon>
												</v-btn>
											</template>
										</v-hover>
									</div>
								</template>
								<span>{{ isLocked ? "Unlock PO" : "Lock PO" }}</span>
							</v-tooltip>
							<v-tooltip bottom v-if="!isLocked">
								<template v-slot:activator="{ on }">
									<v-btn v-on="on" depressed @click="editVendor = true" icon>
										<v-icon> mdi-pencil </v-icon>
									</v-btn>
								</template>
								Edit Vendor
							</v-tooltip>
						</v-col>
					</v-row>

					<v-row>
						<v-col cols="3">
							<div class="form-input">
								<label>Vendor Contact</label>
								<v-select
									v-model="poData.vendorContactId"
									:items="contacts"
									item-text="firstName"
									item-value="vendorContactId"
									dense
									outlined
									hide-details="auto"
									:disabled="isLocked"
								>
									<template v-slot:selection="{ item }">
										<v-row no-gutters align="center">
											<span>{{ `${item.firstName} ${item.lastName}` }}</span>
										</v-row>
									</template>
									<template v-slot:item="{ item, attrs, on }">
										<v-list-item v-on="on" v-bind="attrs">
											<v-list-item-content>
												<v-list-item-title>
													<v-row no-gutters align="center">
														<span>{{ `${item.firstName} ${item.lastName}` }}</span>
													</v-row>
												</v-list-item-title>
											</v-list-item-content>
										</v-list-item>
									</template>
								</v-select>
							</div>
						</v-col>

						<v-col cols="3">
							<div class="form-input">
								<label>Vendor #</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="poData.vendorId"
									:disabled="isLocked"
								></v-text-field>
							</div>
						</v-col>

						<v-col cols="3">
							<div class="form-input">
								<label>Purchase Order #</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="poData.orderNumber"
									disabled
								>
									<template v-if="poData.orderNumber" v-slot:append-outer>
										<div style="display: flex; gap: 10px">
											<template v-if="isValidPurchaseOrder">
												<v-tooltip bottom>
													<template v-slot:activator="{ on }">
														<router-link
															:to="{ path: `/sales-orders/orders/${salesOrder?.salesOrderId}` }"
															><v-icon class="to-order ml-2" v-on="on">
																mdi-clipboard-edit-outline
															</v-icon></router-link
														>
													</template>
													Go to Order
												</v-tooltip>
											</template>
											<v-tooltip bottom>
												<template v-slot:activator="{ on }">
													<router-link
														:to="{
															path: `/receiving/receiving-entry/${poData.purchaseOrderId}`,
														}"
														><v-icon class="to-order" v-on="on">
															mdi-hand-coin
														</v-icon></router-link
													>
												</template>
												Go to Receiving
											</v-tooltip>
										</div>
									</template>
								</v-text-field>
							</div>
						</v-col>
					</v-row>

					<div class="po-form-divider">&nbsp;</div>

					<v-row>
						<v-col cols="4">
							<div class="form-input">
								<label>Bill To</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									value="TEAMLEADER"
									:disabled="isLocked"
								></v-text-field>
							</div>
						</v-col>

						<v-col cols="2">
							<div class="form-input">
								<label>Order Date</label>
								<v-menu
									v-model="showOrderDate"
									:close-on-content-click="false"
									transition="scale-transition"
									offset-y
									min-width="290px"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="formattedOrderDate"
											append-icon="mdi-calendar"
											@click:append="showOrderDate = true"
											outlined
											dense
											v-bind="attrs"
											v-on="on"
											@blur="poData.orderDate = parseDate(formattedOrderDate)"
											hide-details="auto"
											:error="
												errors &&
												errors.orderDate &&
												errors.orderDate !== '' &&
												errors.orderDate !== null &&
												errors.orderDate !== undefined
													? true
													: false
											"
											:error-messages="errors && errors.orderDate ? errors.orderDate : ''"
											:disabled="isLocked"
										></v-text-field>
									</template>
									<v-date-picker
										v-model="poData.orderDate"
										@input="showOrderDate = false"
									></v-date-picker>
								</v-menu>
							</div>
						</v-col>

						<v-col cols="3">
							<div class="form-input">
								<label>Order Type</label>
								<v-select
									v-model="poData.processTypeId"
									:items="orderTypes"
									item-text="text"
									item-value="processTypeId"
									dense
									outlined
									hide-detail
									:disabled="isLocked"
								></v-select>
							</div>
						</v-col>

						<v-col cols="3">
							<div class="form-input">
								<label class="mb-1">Dropship</label>
								<v-checkbox
									class="form-checkbox-center"
									v-model="poData.dropShip"
									color="accent"
									hide-details="auto"
									@change="setShipToAddress()"
									:disabled="isLocked"
								></v-checkbox>
							</div>
						</v-col>

						<v-col cols="4">
							<div class="form-input">
								<label>Ship To</label>
								<v-text-field
									v-if="!poData.dropShip"
									outlined
									dense
									hide-details="auto"
									value="TEAMLEADER"
									:disabled="isLocked"
								></v-text-field>
								<v-combobox
									v-else
									prepend-inner-icon="mdi-magnify"
									dense
									outlined
									hide-details="auto"
									v-model="poData.shipTo"
									:items="shipToResults"
									:item-text="renderAddressLabel"
									@change="setShipToAddress()"
									@keyup="searchCustomerAddresses($event.srcElement.value)"
									:error="
										errors &&
										errors.shiptToAddressLine1 !== '' &&
										errors.shiptToAddressLine1 !== null &&
										errors.shiptToAddressLine1 !== undefined
											? true
											: false
									"
									:disabled="isLocked"
								>
									<template v-slot:item="{ item, attrs, on }">
										<v-list-item v-on="on" v-bind="attrs">
											<v-list-item-content>
												<v-list-item-title>
													<v-row no-gutters align="center">
														{{ `${item.customerName}: ${item.firstName} ${item.lastName}` }}(
														{{
															`${item.addressLine1}, ${item.city}, ${item.state} ${item.postalCode}`
														}}
														)
														<v-icon
															v-if="item.primaryContact && item.isPrimaryAddress"
															small
															class="mx-1"
															>mdi-star</v-icon
														>
													</v-row>
												</v-list-item-title>
											</v-list-item-content>
										</v-list-item>
									</template>
								</v-combobox>
								<p v-if="errors.shiptToAddressLine1" class="validation-error">
									{{ errors.shiptToAddressLine1 }}
								</p>
							</div>
						</v-col>

						<v-col cols="2">
							<div class="form-input">
								<label>Expected Ship Date</label>
								<v-menu
									v-model="showExpectedShipDate"
									:close-on-content-click="false"
									transition="scale-transition"
									offset-y
									min-width="290px"
									:disabled="isLocked"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="formattedExpectedShipDate"
											append-icon="mdi-calendar"
											@click:append="showExpectedShipDate = true"
											outlined
											dense
											v-bind="attrs"
											v-on="on"
											@blur="poData.expectedShipDate = parseDate(formattedExpectedShipDate)"
											hide-details="auto"
											:disabled="isLocked"
										></v-text-field>
									</template>
									<v-date-picker
										v-model="poData.expectedShipDate"
										@input="showExpectedShipDate = false"
									></v-date-picker>
								</v-menu>
							</div>
						</v-col>

						<v-col v-if="poData.dropShip" cols="3">
							<div class="form-input">
								<label>Customer #</label>
								<v-text-field
									outlined
									dense
									hide-details="auto"
									v-model="poData.customerNumber"
									:disabled="isLocked"
								></v-text-field>
							</div>
						</v-col>

						<v-col v-if="poData.dropShip" cols="3">
							<div class="form-input">
								<label>Shipping Method</label>
								<v-select
									v-model="poData.shippingMethodId"
									:items="shippingMethods"
									item-text="shippingMethodName"
									item-value="shippingMethodId"
									dense
									outlined
									hide-detail
									:disabled="isLocked"
									:error="
										errors &&
										errors.shippingMethodId !== '' &&
										errors.shippingMethodId !== null &&
										errors.shippingMethodId !== undefined
											? true
											: false
									"
								></v-select>
								<p v-if="errors.shippingMethodId" class="validation-error">
									{{ errors.shippingMethodId }}
								</p>
							</div>
						</v-col>
					</v-row>
				</template>
			</div>
		</v-col>

		<v-col cols="12" class="flex-auto">
			<PurchaseEntryTable
				ref="purchaseEntryTable"
				v-if="loadingTableData == false && poData && poData.items && poData.vendorId"
				:update="loadingTableData"
				:isLocked="isLocked"
				:tableData="poData.items"
				:errors="errors"
				@clearErrors="clearErrors"
				@updatedTableData="updateTableItems($event)"
			/>
		</v-col>

		<v-col cols="12" class="flex-none d-flex">
			<div class="po-notes" style="width: 50%">
				<div class="po-notes-heading">
					<h2>Order Notes</h2>
					<template v-if="showNoteDropdown">
						<v-btn depressed outlined @click="showNoteDropdown = false">Cancel</v-btn>
						<v-btn :disabled="!canUseNotes()" depressed outlined @click="saveNote()">Save</v-btn>
					</template>
					<v-btn v-else :disabled="!canUseNotes()" depressed outlined @click="newNote()">New</v-btn>
				</div>
				<div
					class="po-notes-dropdown"
					:class="showNoteDropdown ? 'po-notes-dropdown-active' : null"
				>
					<div class="form-textarea">
						<label>Message</label>
						<v-textarea outlined hide-details="auto" rows="12" v-model="noteData.note"></v-textarea>
					</div>
					<div class="po-notes-dropdown-author">
						<h3>Added By:</h3>
						<p>{{ noteData.createdByUser }}</p>
					</div>
				</div>
				<div class="po-notes-container">
					<div class="po-notes-container-list">
						<div v-for="note in poData.notes" :key="note.noteId" class="po-notes-container-note">
							<span
								class="po-notes-container-note-type"
								:class="
									note.noteType === 1 || note.noteType === 'USER'
										? 'po-notes-container-note-type-2'
										: null
								"
								>{{
									note.noteType === 1 || note.noteType === "USER" ? "User Note" : "System Note"
								}}</span
							>
							<p>{{ note.note }}</p>
							<div class="po-notes-container-note-author">
								{{ formatDate(note.noteDate) }} - {{ note.createdByUser }}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="po-pricing" style="width: 50%">
				<div class="po-actions mt-4">
					<v-btn
						depressed
						outlined
						@click="hasUnsavedChanges ? saveAndPrint() : navigateToPrint()"
						:disabled="isSubmitting || !poData.orderNumber || Object.keys(errors).length > 0 || !canPrintPO() || (hasUnsavedChanges && !canUpdatePO())"
					>
						{{ hasUnsavedChanges ? 'Save & Print' : 'Print' }}
					</v-btn>
					<v-btn
						depressed
						color="success"
						@click="submitPO()"
						:disabled="isSubmitting || Object.keys(errors).length > 0 || isLocked || !canPlacePO()"
						>Place PO</v-btn
					>
				</div>
			</div>
		</v-col>

		<!-- warning modal -->
		<v-overlay v-if="itemWarning !== null" :dark="false" :absolute="false" :value="true">
			<div class="input-modal print-modal">
				<div class="input-modal-header">
					<v-btn icon @click="itemWarning = null">
						<v-icon v-text="'mdi-close'"></v-icon>
					</v-btn>
				</div>
				<div class="input-modal-content">
					<div class="text-center">
						<p>
							<b>{{ itemWarning.styleCode }}</b> is not supplied by the current vendor.
						</p>
						<p>Do you want to include it anyway?</p>
					</div>

					<div class="input-modal-content-actions">
						<v-btn color="accent" depressed @click="itemWarning = null">Yes</v-btn>
						<v-btn outlined depressed @click="removeItemWarning()">No</v-btn>
					</div>
				</div>
			</div>
		</v-overlay>
	</v-row>

	<POPrintTemplate
		v-else-if="showPrintPreview"
		:purchaseOrderIds="poData.id > 0 ? [poData.id] : []"
		@cancel="$router.back()"
	/>
</template>

<script>
import gql from "graphql-tag";
import queries from "../../gql/queries";
import DateFormatting from "@/mixins/DateFormatting";
import PurchaseEntryTable from "@/components/Purchasing/PurchaseEntryTable.vue";
import POPrintTemplate from "@/components/Purchasing/POPrintTemplate.vue";
import { mapState } from "vuex";
import MixinPOstatus from "@/mixins/POStatus";
import StatusBadge from "@/components/StatusBadge.vue";
import { PURCHASE_ORDER_BY_ID, CUSTOMER_ADDRESS_BY_ID, GET_SALES_ORDER } from "@/gql/queries";
import { SEARCH_VENDORS, VENDOR_BY_ID } from "@/gql/vendors";
import { validateSizing } from "@/helpers/validator";
import { hasValidColorSelections } from "@/DataTypes";
import { hasPermission, hasAnyPermission, SystemPermissions } from "@/models/User";
import { getApiUrl } from "@/helpers";

export default {
	name: "PurchaseOrderView",
	mixins: [DateFormatting, MixinPOstatus],
	props: {
		purchaseOrderId: {
			type: Number,
			default() {
				return null;
			},
		},
	},
	components: {
		PurchaseEntryTable,
		POPrintTemplate,
		StatusBadge,
	},
	data() {
		return {
			loadingTableData: false,
			vendorSearch: "",
			vendorResults: [],
			editVendor: false,
			showOrderDate: false,
			formattedOrderDate: this.formatDate(
				new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10)
			),
			showExpectedShipDate: false,
			formattedExpectedShipDate: "",
			shipToResults: [],
			purchaseOrder: {},
			poData: {
				purchaseOrderId: null,
				companyName: this.purchaseOrder?.companyName || "",
				vendorId: null,
				vendorContactId: null,
				vendorAddress: {},
				customerNumber: null,
				orderNumber: "",
				statusId: this.purchaseOrder?.orderStatus,
				billTo: null,
				shipTo: null,
				shippingMethodId: null,
				orderDate: "",
				expectedShipDate: "",
				processTypeId: 1,
				dropShip: false,
				items: [],
				notes: [],
			},
			shippingHandling: 0.0,
			shippingHandlingDiscount: 0.0,
			salesTax: 0.0,
			showNoteDropdown: false,
			noteData: {
				noteId: null,
				noteType: 1,
				noteDate: "",
				note: "",
				author: "",
			},
			addresses: [],
			contacts: [],
			shippingMethods: [],
			purchaseOrderData: [],
			orderTypes: [
				{ text: "Standard", processTypeId: 1 },
				{ text: "Attaching", processTypeId: 2 },
				{ text: "Imprint", processTypeId: 3 },
				{ text: "Sewing", processTypeId: 4 },
				{ text: "Monogram", processTypeId: 5 },
			],
			itemWarning: null,
			errors: {},
			isSubmitting: false,
			isLocked: false,
			lockConfirmation: false,
			salesOrder: null,
			isLoaded: false,
			initialState: null,
		};
	},
	apollo: {
		shippingMethods: {
			query() {
				return gql`
					query {
						shippingMethods(take: 500) {
							items {
								shippingMethodId
								shippingMethodName
							}
						}
					}
				`;
			},
			update: (data) => data.shippingMethods.items,
		},
		letterPositions: {
			query() {
				return gql`
					query {
						letterPosition(take: 500) {
							items {
								letterPositionId
								code
								description
							}
						}
					}
				`;
			},
			update: (data) => data.letterPosition.items,
		},
	},
	computed: {
		...mapState(["users"]),
		canViewPrinterSettings() {
			const user = this.$store.getters.userInfo;
			return user?.isAdmin || hasPermission(user, SystemPermissions.Settings_Printers_ALL);
		},
		poToGenerateIds() {
			let ids = this.$store.getters.manualPoItemList.reduce(
				(allIds, item) => allIds.concat(item.ids),
				[]
			);
			let uniqueIds = [...new Set(ids)];

			return this.$route.query.generateManualPo ? uniqueIds : null;
		},
		isValidPurchaseOrder() {
			return this.salesOrder?.salesOrderId;
		},
		orderNumberQuery() {
			if (this.poData.orderNumber) {
				const parts = this.poData.orderNumber.split("-");
				return parts[0];
			}
			return "";
		},
		showPrintPreview() {
			return this.$route.path.endsWith("/print") && this.isLoaded;
		},
		hasUnsavedChanges() {
			if (!this.initialState) return false;
			return JSON.stringify(this.poData) !== JSON.stringify(this.initialState);
		},
	},
	watch: {
		"noteData.noteDate"(value) {
			this.formattedNoteDate = this.formatDate(value);
		},
		"poData.orderDate"(value) {
			this.formattedOrderDate = this.formatDate(value);
		},
		"poData.expectedShipDate"(value) {
			this.formattedExpectedShipDate = this.formatDate(value);
		},
		$route() {
			this.init();
		},
	},
	methods: {
		// <security>
		canPlacePO() {
			const user = this.$store.getters.userInfo;
			if (
				hasAnyPermission(user, [
					SystemPermissions.Purchasing_PurchaseOrders_PlacePO,
					SystemPermissions.Purchasing_POsToGenerate_PlaceStockPO,
				])
			) {
				return true;
			}
			return user.isAdmin;
		},
		canUpdatePO() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Purchasing_PurchaseOrders_Update_Unlock)) {
				return true;
			}
			return user.isAdmin;
		},
		canCancelPO() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Purchasing_PurchaseOrders_CancelPO)) {
				return true;
			}
			return user.isAdmin;
		},
		canUseNotes() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Purchasing_PurchaseOrder_Notes_ALL)) {
				return true;
			}
			return user.isAdmin;
		},
		canPrintPO() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Purchasing_PurchaseOrders_Print)) {
				return true;
			}
			return user.isAdmin;
		},
		// </security>

		reset() {
			this.loadingTableData = false;
			this.vendorSearch = "";
			this.vendorResults = [];
			this.showOrderDate = false;
			this.formattedOrderDate = this.formatDate(
				new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10)
			);
			this.showExpectedShipDate = false;
			this.formattedExpectedShipDate = "";
			this.shipToResults = [];
			this.poData = {
				purchaseOrderId: null,
				companyName: "",
				vendorId: null,
				vendorContactId: null,
				vendorAddress: {},
				customerNumber: null,
				orderNumber: "",
				statusId: null,
				billTo: null,
				shipTo: null,
				shippingMethodId: null,
				orderDate: "",
				expectedShipDate: "",
				processTypeId: 1,
				dropShip: false,
				items: [],
				notes: [],
			};
			this.shippingHandling = 0.0;
			this.shippingHandlingDiscount = 0.0;
			this.salesTax = 0.0;
			this.showNoteDropdown = false;
			this.noteData = {
				noteId: null,
				noteType: 1,
				noteDate: "",
				note: "",
			};
		},

		async getPurchaseOrderData(purchaseOrder) {
			const res = await this.$apollo.query({
				fetchPolicy: "no-cache",
				query: PURCHASE_ORDER_BY_ID,
				variables: { id: purchaseOrder.purchaseOrderId },
			});

			console.log("Purchase order data....", res);

			if (res && res.data && res.data.purchaseOrderById) {
				const poData = res.data.purchaseOrderById;

				await this.getAndSetVendorById(poData.vendorId);
				await this.getLineItemData(poData);

				let shipTo;
				if (poData.salesOrderId) {
					await this.setSalesOrder(poData.salesOrderId);
					await this.setShipToResults();

					if (poData.dropShip && this.salesOrder?.customer?.customer) {
						if (poData.customerAddressId && poData.contactId) {
							shipTo = await this.findShipTo(poData.customerAddressId, poData.contactId);
						} else {
							shipTo = await this.findShipTo(
								this.salesOrder?.customer?.shipTo,
								this.salesOrder?.customer?.contactId
							);
						}
					}
				} else if (poData.customerAddressId) {
					shipTo = await this.findShipTo(poData.customerAddressId, poData.contactId);
				}

				this.poData = {
					...this.poData,
					...poData,
					purchaseOrderId: poData.id,
					expectedShipDate: poData.expectedDate,
					orderNumber: poData.purchaseOrderNumber,
					orderDate: poData.purchaseOrderDate,
					customerNumber: poData.salesOrderId,
					shipTo,
					notes: await Promise.all(
						poData.notes.map(async (note) => ({
							...note,
							noteDate: note.createdAt,
							note: note.noteText,
							author: note.createdBy,
							createdByUser: await this.getUserName(note.createdBy),
						}))
					),
					items: await Promise.all(
						poData.items.map(async (item) => {
							// Fetch live colorOptions and sizeChart from inventory item
							const inventoryData = await this.fetchInventoryItemData(item.itemOrdered.inventoryItemId);

							return {
								...item,
								quantity: item.quantity,
								unitPrice: item.unitPrice,
								totalSizes: item.totalSizes,
								hasSizes: item.hasSizes,
								itemOrdered: item.itemOrdered,
								sizeChart: inventoryData?.sizeChart || null,
								colorOptions: inventoryData?.colorOptions || [],
								lettering: item.lettering,
								sizeSelections: item.sizes.map((size) => ({
									...size,
									name: size.size,
									length: size.length,
									quantity: size.quantity,
								})),
								specialSizeSelections: item.specialSizes,
								colorSelections: item.colors,
								freeTextSelections: item.freeTextSelections,
								styleCode: item.itemOrdered.styleCode,
								systemCategoryId:
									this.$store.state.systemCategories.systemCategories.find(
										(el) => el.name === item.itemOrdered.systemCategory
									)?.systemCategoryId || 0,
								description: item.itemOrdered.description,
								inventoryItemId: item.itemOrdered.inventoryItemId,
								vendorCost: item.unitPrice,
								includeColors: item.colors.length > 0,
								includeSizingChart: item.sizes.length > 0 || item.colors.length > 0,
							};
						})
					),
				};
			}

			await this.setShipToAddress();

			await this.$nextTick(() => {
				this.isLoaded = true;
				this.initialState = JSON.parse(JSON.stringify(this.poData));
			});
		},

		async fetchInventoryItemData(inventoryItemId) {
			try {
				// First try to get as a regular inventory item
				const itemRes = await this.$apollo.query({
					query: gql`
						query GetInventoryItemById($id: Int!) {
							inventoryItemById(id: $id) {
								colorOptions {
									colorOptionId
									name
									description
									position
									type
									colorPalettes {
										colorPaletteId
										name
										description
										upcharge
										colors {
											colorId
											label
											description
											hexCode
											isRetired
											isVisible
											isCustomColor
										}
									}
								}
								sizeChart {
									sizeChartId
									name
									description
									includeLengths
									sizes {
										sizeId
										name
										showSize
										sequence
										isRetired
										lengths {
											sizeLengthId
											length
											sizeId
											isRetired
										}
									}
								}
							}
						}
					`,
					variables: {
						id: inventoryItemId,
					},
				});

				// If found as regular item, return it
				if (itemRes.data.inventoryItemById) {
					return itemRes.data.inventoryItemById;
				}

				// If not found as regular item, try as subitem
				const subItemRes = await this.$apollo.query({
					query: gql`
						query GetSubItemById($id: Int!) {
							subItemById(id: $id) {
								colorOptions {
									colorOptionId
									name
									description
									position
									type
									colorPalettes {
										colorPaletteId
										name
										description
										upcharge
										colors {
											colorId
											label
											description
											hexCode
											isRetired
											isVisible
											isCustomColor
										}
									}
								}
								sizeChart {
									sizeChartId
									name
									description
									includeLengths
									sizes {
										sizeId
										name
										showSize
										sequence
										isRetired
										lengths {
											sizeLengthId
											length
											sizeId
											isRetired
										}
									}
								}
							}
						}
					`,
					variables: {
						id: inventoryItemId,
					},
				});

				return subItemRes.data.subItemById;
			} catch (error) {
				console.error('Error fetching inventory item data:', error);
				return null;
			}
		},

		async findShipTo(customerAddressId, contactId) {
			const customer = await this.$apollo.query({
				query: CUSTOMER_ADDRESS_BY_ID,
				variables: { id: customerAddressId || this.customer.shipTo },
			});

			const customerId = customer?.data?.customerAddressById?.customer?.customerId;

			if (customerId) {
				await this.getCustomerById(customerId);
			} else {
				await this.searchCustomerAddresses(
					customer?.data?.customerAddressById?.customer.organization
				);
			}

			return this.shipToResults?.find(
				(str) => str.addressId === customerAddressId && str.contactId === contactId
			);
		},

		async getLineItemData(poData) {
			const res = poData.salesOrderId
				? await this.$apollo.query({
						query: gql`
				query {
					orderLineItemsRecursive(id: ${poData.salesOrderId} ){
						itemId,
						lettering {
						letterPositionId
						text
						}
					}
				}
				`,
				  })
				: null;

			if (res && res.data) {
				let matchedLineItem = null;
				let matchedPOItem = null;
				let matchedPOItemIdx = null;

				this.poData.items.forEach((poItem, index) => {
					res.data.orderLineItemsRecursive.forEach((lineItem) => {
						if (poItem.itemOrdered.inventoryItemId === lineItem.itemId) {
							matchedLineItem = lineItem;
							matchedPOItem = poItem;
							matchedPOItemIdx = index;
						}
					});
				});

				// set lettering data
				if (this.letterPositions && matchedLineItem && matchedLineItem.lettering) {
					let letterPositionById = this.letterPositions.find(
						(s) => s.letterPositionId == matchedLineItem.lettering.letterPositionId
					);
					this.matchedLettering = { ...matchedLineItem.lettering, ...letterPositionById };
				}

				this.salesOrderData = res.data;
				this.matchedPOItem = matchedPOItem;
				this.matchedPOItemIdx = matchedPOItemIdx;
			}
		},

		updateTableItems(itemsObj) {
			//update the items data for po
			this.poData.items = [...itemsObj.tableItems];

			//check if a vendor was pre selected before items were added to po
			if (!this.poData.vendorId && itemsObj.newTableItem) {
				//if no vendorId was assigned and new item was added, set the added items vendor to po
				this.getAndSetVendorById(itemsObj.newTableItem.vendorId);
			} else if (
				this.poData.vendorId &&
				itemsObj.newTableItem &&
				this.poData.vendorId !== itemsObj.newTableItem.vendorId
			) {
				//if vendorId is assigned and a new added item doesnt come from the same vendor set the item as a warning and show modal
				this.itemWarning = itemsObj.newTableItem;
			}
		},

		renderAddressLabel(item) {
			return `${item.customerName}: ${item.firstName} ${item.lastName} (${item.addressLine1}, ${item.city}, ${item.state} ${item.postalCode})`;
		},

		newNote() {
			this.showNoteDropdown = true;
			this.noteData = {
				noteId: null,
				noteType: 1,
				noteDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
					.toISOString()
					.substr(0, 10),
				note: "",
				author: this.$store.getters.userInfo.username || this.$store.getters.userInfo.name,
				createdByUser: this.$store.getters.userInfo.username || this.$store.getters.userInfo.name,
			};
		},

		save() {
			this.submitPO(true);
		},
		navigateToPrint() {
			this.$router.push(`/purchasing/purchase-orders/${this.poData.purchaseOrderId}/print`);
		},
		async saveAndPrint() {
			this.isSubmitting = true;
			try {
				if (!this.validate()) return;

				const res = await this.$http.put(
					getApiUrl(`/api/purchasing/orders/${this.poData.purchaseOrderId}`),
					this.shapeDataRequest()
				);

				if (res && res.status === 200 && res.data) {
					this.poData.statusId = 1;
					this.purchaseOrder.orderStatus = 1;
					this.poData.orderNumber = res.data.purchaseOrderNumber;
					await this.submitNotes(res.data.purchaseOrderId);
					this.$store.dispatch("updateManualPoItems", []);
					this.initialState = JSON.parse(JSON.stringify(this.poData));
					this.navigateToPrint();
				} else {
					alert("Error submitting purchase order");
				}
			} catch (error) {
				console.log(error);
			} finally {
				this.isSubmitting = false;
			}
		},

		async handleCancelPO() {
			try {
				const res = await this.$http["put"](
					getApiUrl(`/api/purchasing/orders/${this.poData.purchaseOrderId}/cancel`)
				);

				if (res && res.status === 200 && res.data) {
					this.poData.orderNumber = res.data.purchaseOrderNumber;
					this.$router.back();
				} else {
					alert("Error cancelling purchase order");
				}
			} catch (error) {
				console.log(error);
			}
		},

		async handleRestorePO() {
			try {
				const res = await this.$http["put"](
					getApiUrl(`/api/purchasing/orders/${this.poData.purchaseOrderId}/restore`)
				);

				if (res && res.status === 200 && res.data) {
					this.poData.orderNumber = res.data.purchaseOrderNumber;
					this.$router.back();
				} else {
					alert("Error restoring purchase order");
				}
			} catch (error) {
				console.log(error);
			}
		},

		async saveNote() {
			//add current active note to quote notes
			this.noteData.noteDate = `${this.noteData.noteDate} ${new Date().toLocaleTimeString()}`;
			this.poData.notes.push({
				...this.noteData,
				author: this.$store.getters.userInfo.userId,
				createdByUser: this.$store.getters.userInfo.username || this.$store.getters.userInfo.name,
			});

			this.showNoteDropdown = false;

			await this.submitNotes(this.poData.purchaseOrderId);
		},

		clearErrors($event) {
			this.errors = {};

			setTimeout(() => this.validate(), 500);
		},

		setVendor() {
			//assign the selected vendor contacts and addresses
			this.contacts = this.vendorSearch.contacts || [];
			this.addresses = this.vendorSearch.addresses || [];

			//assign the selected vendor data to poData
			if (this.contacts.some((obj) => obj.primaryContact == true)) {
				//assign the first found primary contact
				this.poData.vendorContactId = this.contacts.find(
					(obj) => obj.primaryContact == true
				)?.vendorContactId;
			} else {
				//assign a default first contact
				this.poData.vendorContactId = this.contacts[0]?.vendorContactId;
			}

			//always assign bill to teamleader address
			this.poData.billTo = {
				organization: "TEAMLEADER",
				addresses: [
					{
						vendorAddressId: 1,
						label: "TEAMLEADER",
						addressLine1: "2901 SUMMIT AVE",
						addressLine2: "#300",
						addressTypeId: null,
						city: "PLANO",
						state: "TX",
						postalCode: 75074,
					},
				],
			};

			this.poData.vendorId = this.vendorSearch.vendorId;
			this.poData.vendorAddress =
				this.vendorSearch.addresses?.find((el) => el.isPrimaryAddress) ??
				this.vendorSearch.addresses?.length > 0
					? this.vendorSearch.addresses[0]
					: {};
			this.poData.companyName = this.vendorSearch.companyName;
			this.poData.processTypeId = 1;
			if (!this.poData.purchaseOrderId) {
				this.poData.orderDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
					.toISOString()
					.substr(0, 10);
			}
			this.editVendor = false;
		},

		async setShipToAddress() {
			this.clearErrors();

			if (this.poData.dropShip) {
				// If we don't already have shipTo data and we have a sales order, try to get it from there
				if (!this.poData.shipTo && this.salesOrder?.customer) {
					try {
						// Get ship to info from customer address and contact
						const shipTo = await this.findShipTo(
							this.salesOrder?.customer?.shipTo,
							this.salesOrder?.customer?.contactId
						);
						
						this.poData.shipTo = shipTo;

						// Set related fields if we have shipTo data
						if (this.poData.shipTo) {
							this.poData.customerNumber =
								this.poData.shipTo.customerNumber ||
								this.salesOrder?.customer?.customer?.customerNumber;
							this.poData.shippingMethodId = this.poData.shippingMethodId || 1;
						}
					} catch (error) {
						console.error("Error setting ship to address:", error);
					}
				}
			} else {
				// If not dropship, set to TeamLeader
				this.poData.shipTo = {
					organization: "TEAMLEADER",
					addresses: [
						{
							vendorAddressId: 1,
							label: "TEAMLEADER",
							addressLine1: "2901 SUMMIT AVE",
							addressLine2: "#300",
							addressTypeId: null,
							city: "PLANO",
							state: "TX",
							postalCode: 75074,
						},
					],
				};
				this.poData.customerNumber = null;
				this.poData.shippingMethodId = null;
			}
		},

		removeItemWarning() {
			this.loadingTableData = true;

			//remove the itemWarning obj from the po items table
			this.poData.items = this.poData.items.filter((obj) => obj !== this.itemWarning);
			this.itemWarning = null;

			this.$nextTick(() => (this.loadingTableData = false));
		},

		async searchVendors(input) {
			try {
				const res = await this.$apollo.query({
					fetchPolicy: "no-cache",
					query: SEARCH_VENDORS,
					variables: {
						searchTerm: `${input}`,
					},
				});
				this.vendorResults = res.data.response.items;
			} catch (error) {
				console.log(error);
			}
		},

		async searchCustomerAddresses(input) {
			const response = await this.$apollo.query({
				query: gql`
		                query {
		                    searchCustomers(searchTerm: "${input}") {
		                        items {
		                            customerId
		                            customerNumber
		                            organization
		                            addresses {
		                                addressId
		                                label
		                                addressLine1
		                                addressLine2
		                                addressTypeId
		                                city
		                                state
		                                postalCode
										isPrimaryAddress
		                            }
									contacts {
										contactId
										contactTypeId
										primaryContact
										firstName
										middleName
										lastName
										workPhoneNumber
										cellPhoneNumber
										email
										isActive
									}
		                        }
		                        pageInfo {
		                            hasNextPage
		                            hasPreviousPage
		                        }
		                    }
		                }
				    `,
				fetchPolicy: "no-cache",
			});

			const customers = response.data.searchCustomers.items;
			this.setShipToResults(customers);
		},

		async getCustomerById(customerId) {
			const response = await this.$apollo.query({
				query: gql`
		                query {
							customerById(id: ${customerId}) {
								customerId
								customerNumber
								organization
								addresses {
								addressId
								label
								addressLine1
								addressLine2
								addressTypeId
								city
								state
								postalCode
								}
								contacts {
								contactId
								contactTypeId
								primaryContact
								firstName
								middleName
								lastName
								workPhoneNumber
								cellPhoneNumber
								email
								isActive
								}
							}
							}

				    `,
				fetchPolicy: "no-cache",
			});

			const customers = response.data.customerById;
			this.setShipToResults(customers);
		},

		setShipToResults(customers) {
			if (!customers && !this.salesOrder?.customer?.customer) {
				console.error("Customer is undefined:", customers);
				this.shipToResults = [];
				return;
			}

			const customerArray =
				customers && Array.isArray(customers)
					? customers
					: customers
					? [customers]
					: [this.salesOrder.customer.customer];

			this.shipToResults = customerArray.flatMap((customer) => {
				const addresses = Array.isArray(customer.addresses)
					? customer.addresses.map((address) => ({
							...address,
							customerName: customer.organization,
							isVendor: false,
					  }))
					: [];

				const contacts = Array.isArray(customer.contacts)
					? customer.contacts.map((contact) => ({
							...contact,
							customerName: customer.organization,
							isVendor: false,
					  }))
					: [];

				return contacts.flatMap((contact) =>
					addresses.map((address) => ({
						...contact,
						...address,
					}))
				);
			});
		},

		async setSalesOrder(salesOrderId) {
			const orderRes = await this.$apollo.query({
				query: GET_SALES_ORDER,
				variables: {
					id: salesOrderId,
				},
				fetchPolicy: "no-cache",
			});

			this.salesOrder = orderRes.data.salesOrderById;
		},

		async getAndSetVendorById(vendorId) {
			try {
				const res = await this.$apollo.query({
					fetchPolicy: "no-cache",
					query: VENDOR_BY_ID,
					variables: {
						id: vendorId,
					},
				});

				this.vendorSearch = res.data.response;

				//set the assigned vendor data
				this.setVendor();
			} catch (error) {
				console.log(error);
			}
		},

		validate() {
			let errors = {};

			if (this.poData.vendorId && this.poData.vendorId !== "") {
				if (this.poData.orderDate === "" || this.poData.orderDate === null) {
					errors.orderDate = "Order Date is required";
				}

				if (this.poData.dropShip) {
					if (this.poData.shipTo === null) {
						errors.shiptToAddressLine1 = "required";
					}

					if (this.poData.shippingMethodId === null) {
						errors.shippingMethodId = "required";
					}
				}
			} else {
				errors.vendorId = "Vendor is required";
			}

			if (this.poData.items && this.poData.items.length > 0) {
				this.poData.items.forEach((item, index) => {
					if (parseFloat(item.quantity) < 1) {
						errors[`item${index}quantity`] = "must be more than 0";
					}

					if (item.quantity === "") {
						errors[`item${index}quantity`] = "";
					}

					if (item.styleCode === "") {
						errors[`item${index}styleCode`] = "";
					}

					if (!hasValidColorSelections(item)) {
						errors[`item${index}color`] = "required";
					}

					if (item.sizeChart && item.sizeChart.sizes.length > 0 && item.quantity > 0) {
						if (!validateSizing(item)) {
							errors[`item${index}size`] = "required";
						}
					}
				});
			} else {
				errors.items = "At least one item is required";
			}

			this.errors = errors;
			console.log(errors);

			const passed = Object.keys(errors).length === 0;

			// TODO [BD|2023-02-01] - Maybe remove this added side-effect
			if (!passed) this.isSubmitting = false;

			return passed;
		},

		shapeDataRequest() {
			return {
				vendorId: this.poData.vendorId,
				vendorContactId: this.poData.vendorContactId,
				dropShip: this.poData.dropShip,
				items: this.poData.items.map((item) => ({
					inventoryItemId: item.itemId ? item.itemId : item.inventoryItemId,
					quantity: item.quantity,
					unitPrice: item.vendorCost,
					sizeSelections: item.sizeSelections
						? item.sizeSelections
								.filter((el) => !!el.quantity && el.sizeId != null)
								.map((selection) => ({
									sizeId: selection.sizeId,
									quantity: selection.quantity,
									length: selection.length,
									sizeLengthId: selection.sizeLengthId,
								}))
						: [],
					specialSizeSelections: item.specialSizeSelections || [],
					colorSelections: item.colorSelections
						? item.colorSelections
								?.filter((cs) => cs.colorId > 0)
								.map((selection) => {
									return {
										colorId: selection.colorId,
									};
								})
						: [],
					freeTextSelections: item.freeTextSelections,
					lettering: item.lettering,
					salesOrderLineItemId: item.salesOrderLineItemId,
					matchPreviousPoNumber: item.matchPreviousPoNumber,
					note: item.note,
					subItems: item.subItems,
				})),
				processTypeId: this.poData.processTypeId,
				orderDate: this.poData.orderDate,
				expectedShipDate: this.poData.expectedShipDate === "" ? null : this.poData.expectedShipDate,
				contactId: this.poData?.shipTo?.contactId,
				customerAddressId: this.poData?.shipTo?.addressId ?? this.vendorSearch.vendorAddressId,
				shippingMethodId: this.poData.shippingMethodId,
				poToGenerateIds: this.poToGenerateIds,
			};
		},

		async submitNotes(orderId) {
			try {
				if (!this.poData.notes.length) return;
				let notes = this.poData.notes.map((note) => {
					let noteData = {
						text: note.note,
					};

					return noteData;
				});

				const res = await this.$http.put(`/api/purchasing/orders/${orderId}/notes`, notes);
				console.log(res);
			} catch (error) {
				console.log(error);
			}
		},

		async submitPO(isUpdate = false) {
			this.isSubmitting = true;
			try {
				if (!this.validate()) return;

				const res = await this.$http[isUpdate ? "put" : "post"](
					getApiUrl(`/api/purchasing/orders/${
						isUpdate ? this.poData.purchaseOrderId : ""
					}`),
					this.shapeDataRequest()
				);

				if (res && res.status === 200 && res.data) {
					this.poData.statusId = 1;
					this.purchaseOrder.orderStatus = 1; // Set status so StatusBadge shows "On Order" instead of "Error"
					this.poData.orderNumber = res.data.purchaseOrderNumber;
					this.isSubmitting = false;

					await this.submitNotes(res.data.purchaseOrderId);

					// clear out any manual PO Items from POs to Generate
					this.$store.dispatch("updateManualPoItems", []);

					this.$router.back();
				} else {
					alert("Error submitting purchase order");
					this.isSubmitting = false;
				}
			} catch (error) {
				console.log(error);
				this.isSubmitting = false;
			}
		},
		async getUserName(id) {
			try {
				const response = await this.$apollo.query({
					query: gql`
						query GetUserName($userId: Int!) {
							userNameById(userId: $userId) {
								fullName
								username
							}
						}
					`,
					variables: {
						userId: id,
					},
					fetchPolicy: "no-cache",
				});

				const user = response.data.userNameById;
				return user?.fullName || user?.username || "...";
			} catch (error) {
				console.error("Error fetching user:", error);
				return "...";
			}
		},
		async handleLock() {
			try {
				const res = await this.$http.put(
					`/api/purchasing/orders/${this.poData.purchaseOrderId}/lock`,
					JSON.stringify({ isLocked: !this.isLocked }),
					{
						headers: {
							"Content-Type": "application/json",
						},
					}
				);

				if (res && res.status === 200 && res.data) {
					this.poData.orderNumber = res.data.purchaseOrderNumber;
					this.isLocked = res.data.isLocked;
					this.lockConfirmation = true;
					setTimeout(() => {
						this.lockConfirmation = false;
					}, 2000);
				} else {
					alert("Error cancelling purchase order");
				}
			} catch (error) {
				console.log(error);
			}
		},

		findItemInOrderItems(orderItems, lineItemId) {
			for (let item of orderItems) {
				if (item.lineItemId === lineItemId) {
					return item;
				}
				// Check if item has children and recursively search within them
				if (item.children && item.children.length > 0) {
					let found = this.findItemInOrderItems(item.children, lineItemId);
					if (found) {
						return found;
					}
				}
			}
			return null;
		},

		async getOrderItems(manualItem) {
			this.loadingTableData = true;
			try {
				const res = await this.$http.get(`/api/orders/${manualItem.salesOrderId}/lineItems`);
				console.log("lineItems", res);

				// Recursive function to process each item and its children
				const processItem = async (item) => {
					let inventoryItem = await this.getLineItemSizeColorData(item);
					item = { ...item, ...inventoryItem };

					item.priceIncluded = item.includePrice; // some bad/inconsistent data transform

					if (item.lettering) {
						let letterPositionById = this.letterPositions.find(
							(s) => s.letterPositionId == item.lettering.letterPositionId
						);
						item.lettering = { ...item.lettering, ...letterPositionById };
					}

					// Rebuild sizeSelections from manualItem.sizes so that every merged
					// size (across sales orders and line items) survives — previously this
					// filtered the template line item's own sizeSelections, which silently
					// dropped any sizes the template didn't already carry.
					if (manualItem.sizes && manualItem.sizes.length > 0) {
						const originalSizeSelections = Array.isArray(item.sizeSelections)
							? item.sizeSelections
							: [];
						const existingByKey = new Map();
						for (const s of originalSizeSelections) {
							existingByKey.set(`${s.sizeId}-${s.sizeLengthId ?? "null"}`, s);
						}

						let totalQuantity = 0;
						item.sizeSelections = manualItem.sizes.map((ms) => {
							totalQuantity += ms.need;

							const key = `${ms.sizeId}-${ms.sizeLengthId ?? "null"}`;
							const template =
								existingByKey.get(key) ||
								originalSizeSelections.find((s) => s.sizeId === ms.sizeId);

							if (template) {
								return {
									...template,
									sizeLengthId: ms.sizeLengthId ?? template.sizeLengthId,
									quantity: ms.need,
								};
							}

							let sizeChartEntry = null;
							let lengthEntry = null;
							if (item.sizeChart && Array.isArray(item.sizeChart.sizes)) {
								sizeChartEntry = item.sizeChart.sizes.find(
									(sc) => sc.sizeId === ms.sizeId
								);
								if (
									sizeChartEntry &&
									ms.sizeLengthId != null &&
									Array.isArray(sizeChartEntry.lengths)
								) {
									lengthEntry = sizeChartEntry.lengths.find(
										(l) => l.sizeLengthId === ms.sizeLengthId
									);
								}
							}

							return {
								sizeId: ms.sizeId,
								sizeLengthId: ms.sizeLengthId,
								name: sizeChartEntry?.name ?? ms.sizeName,
								length: lengthEntry?.length ?? null,
								quantity: ms.need,
							};
						});
						item.quantity = totalQuantity;
					} else {
						// Default item quantity
						item.quantity = manualItem.need;
					}

					// Process children
					if (item.children && item.children.length > 0) {
						item.children = await Promise.all(item.children.map(processItem));
					}

					return item;
				};

				// Process all top-level items
				const orderItems = await Promise.all(res.data.lineItems.map(processItem));

				let lineItem = this.findItemInOrderItems(orderItems, manualItem.lineItemId);
				if (lineItem) {
					this.poData.items.push(lineItem);
				}
			} catch (error) {
				console.error(error);
			}
		},

		async getLineItemSizeColorData(item) {
			try {
				let res;
				if (this.$systemCategories.isSubItem(item.systemCategoryId)) {
					//if a subitem is passed in we only need colorOptions
					res = await this.$apollo.query({
						query: gql`
							query GetSubItemById($id: Int!) {
								subItemById(id: $id) {
									vendorCost
									colorOptions {
										colorOptionId
										name
										description
										position
										type
										colorPalettes {
											name
											description
											upcharge
											colors {
												label
												description
												hexCode
												isVisible
												colorId
												isRetired
												isCustomColor
											}
										}
									}
								}
							}
						`,
						fetchPolicy: "no-cache",
						variables: {
							id: item.itemId,
						},
					});

					res.data.subItemById.type = "SubItem";
					return res.data.subItemById;
				} else {
					//if a regular ionventory item is passed in we need to get color and size data
					res = await this.$apollo.query({
						query: gql`
							query GetInventoryItemById($id: Int!) {
								inventoryItemById(id: $id) {
									vendorCost
									colorOptions {
										colorOptionId
										name
										description
										position
										type
										colorPalettes {
											name
											description
											upcharge
											colors {
												label
												description
												hexCode
												isVisible
												colorId
												isRetired
												isCustomColor
											}
										}
									}
									sizeChart {
										name
										description
										includeLengths
										sizes {
											lengths {
												length
												sizeId
												sizeLengthId
												isRetired
											}
											name
											showSize
											sizeId
											isRetired
											sequence
										}
									}
								}
							}
						`,
						fetchPolicy: "no-cache",
						variables: {
							id: item.itemId,
						},
					});

					return res.data.inventoryItemById;
				}
			} catch (error) {
				console.log(error);
			}
		},

		async genItem(item) {
			console.log("manualPO", item);
			await this.getOrderItems(item);
		},

		async init() {
			//check if purchaseOrder prop with id is passed in and retrieve that purchaseOrders data
			if (this.purchaseOrderId) {
				this.$apollo
					.query({
						query: queries.SEARCH_PURCHASE_ORDER_ITEMS,
						variables: {
							offset: 0,
							take: 1,
							searchTerm: "",
							where: {
								and: [
									{
										purchaseOrderId: { eq: this.purchaseOrderId },
									},
								],
							},
						},
					})
					.then(async (res) => {
						if (
							res.data.response &&
							res.data.response.items &&
							res.data.response.items.length > 0
						) {
							const po = res.data.response.items[0];
							this.purchaseOrder = po;
							this.isLocked = po.isLocked;
							await this.getPurchaseOrderData(po);
						}
					});
			}

			//if a generateManualPo parameter exists generate a po with the stored purchasing state data ex: user selects items from po's to generates and submits
			if (this.$route.query.generateManualPo) {
				console.log("Generating manual PO from state data...");
				this.getAndSetVendorById(this.$store.getters.manualPoItemList[0].vendorId);

				this.poData.items = [];
				let groupedItems = {};

				// First loop through each item in the manualPoItemList
				this.$store.getters.manualPoItemList.forEach((item) => {
					const style = `${item.styleCode}:${item.styleHash}`;

					// Initialize the group for each styleCode if it doesn't exist
					if (!groupedItems[style]) {
						groupedItems[style] = {
							...item,
							sizes: [],
							need: 0, // We will sum up the "need" quantities
						};
					}

					// Loop through each size in the item and group by sizeId and sizeLengthId
					item.sizes.forEach((size) => {
						// Find if the size already exists in the grouped sizes
						let existingSize = groupedItems[style].sizes.find(
							(s) => s.sizeId === size.sizeId && s.sizeLengthId === size.sizeLengthId
						);

						if (existingSize) {
							// If it exists, sum the "need" quantities
							existingSize.need += size.need;
						} else {
							// If it doesn't exist, add it to the sizes array
							groupedItems[style].sizes.push({ ...size });
						}

						// Add the "need" value to the root "need" property for the style
						groupedItems[style].need += size.need;
					});
				});

				// After grouping, convert the grouped items back to an array
				const consolidatedItems = Object.values(groupedItems);

				// Now pass the consolidated items to genItem
				consolidatedItems.forEach((item) => {
					this.genItem(item);
				});
				this.loadingTableData = false;
			}
		},
	},
	async mounted() {
		await this.init();
	},
};
</script>

<style lang="scss">
.to-order {
	cursor: pointer;

	&:hover {
		color: #1264f6;
	}
}
</style>

<style lang="scss" scoped>
.po {
	&-actions {
		display: flex;
		width: 100%;
		justify-content: flex-end;

		.v-btn {
			background-color: $color-white;
			border-color: $color-gray;
			text-transform: initial;

			&:not(:last-child) {
				margin-right: 0.8rem;
			}
		}
	}

	&-form {
		position: relative;
		display: block;
		background-color: $color-white;
		border: 1px solid $color-gray;
		border-radius: 3px;
		padding: 0.5rem 1.5rem;

		.row {
			margin: initial;
		}

		.form-input {
			margin: initial;
		}

		&-header {
			display: flex;
			align-items: center;

			a {
				color: $color-tertiary;
			}
		}

		&-title {
			display: inline-block;
			font-size: 2.2rem;
			font-weight: 700;
			padding-right: 1rem;
		}
		&-status {
			display: inline-block;
			border: 1px solid;
			font-size: 1.4rem;
			padding: 0.5rem 1rem;
			border: 1px solid;

			&-orange {
				border-color: $color-orange;
				color: $color-orange;
			}

			&-green {
				border-color: $color-green;
				color: $color-green;
			}
		}

		&-divider {
			display: block;
			width: calc(100% - 24px);
			height: 1px;
			margin: 1.2rem auto;
			background-color: $color-gray;
		}
	}

	&-notes {
		position: relative;
		display: inline-flex;
		flex-flow: column;
		width: 50%;
		height: 100%;
		border: 1px solid $color-gray;
		background-color: $color-white;
		margin-right: 1rem;

		&-heading {
			display: flex;
			flex-flow: row nowrap;
			align-items: center;
			width: 100%;
			height: 4.7rem;
			background-color: $color-gray-light-2;
			border-bottom: 1px solid $color-gray;
			padding: 0.5rem 1rem;
			z-index: 1;

			h2 {
				font-size: 1.4rem;
				font-weight: 700;
				text-transform: uppercase;
				margin-right: auto;
			}

			.v-btn {
				background-color: $color-white;

				&:nth-child(3) {
					margin-left: 0.8rem;
				}
			}
		}

		&-container {
			position: absolute;
			padding-top: 4.7rem; //match with heading height
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			flex-grow: 1;
			background-color: $color-white;
			overflow-y: auto;

			&-list {
				display: flex;
				flex-flow: column nowrap;
				width: 100%;
			}

			&-note {
				display: flex;
				flex-flow: row nowrap;
				align-items: center;
				padding: 1rem 0.8rem;
				width: 100%;
				font-size: 1.4rem;
				border-bottom: 1px solid $color-gray;

				p {
					flex: 1 1 auto;
				}

				&-type {
					padding: 0.5rem 1rem;
					min-width: 12rem;
					text-align: center;
					border-radius: 25rem;
					background-color: $color-gray;
					font-weight: 700;
					font-size: 1.4rem;
					margin-right: 1rem;

					&-2 {
						background-color: $color-green;
					}
				}

				&-author {
					margin-left: 1rem;
				}
			}
		}

		&-dropdown {
			position: relative;
			display: flex;
			flex-flow: column;
			background-color: $color-white;
			height: 0%;
			border-bottom: 1px solid transparent;
			overflow: hidden;
			transition: height 0.3s ease-in-out;
			z-index: 5;

			&-active {
				height: 100%;
				box-shadow: rgba(50, 50, 93, 0.05) 0px 2px 5px -1px, rgba(0, 0, 0, 0.2) 0px 1px 3px -1px !important;
			}

			.form-input,
			.form-textarea {
				margin: 1.25rem;
			}

			.form-input {
				display: inline-flex;
				width: calc(50% - 2.5rem);
			}

			.form-textarea {
				label {
					width: 19.5%;
				}
			}

			&-author {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				font-size: 1.4rem;
				margin: 1.25rem;

				h3 {
					font-weight: normal;
					padding-right: 0.5rem;
				}

				p {
					font-size: 1.6rem;
				}
			}
		}
	}

	&-related {
		position: relative;
		display: inline-flex;
		flex-flow: column;
		width: 15%;
		min-width: 20rem;
		height: 100%;
		border: 1px solid $color-gray;
		background-color: $color-white;

		&-heading {
			display: flex;
			flex-flow: row nowrap;
			align-items: center;
			width: 100%;
			height: 4.7rem;
			background-color: $color-gray-light-2;
			border-bottom: 1px solid $color-gray;
			padding: 0.5rem 1rem;
			z-index: 1;

			h2 {
				font-size: 1.4rem;
				font-weight: 700;
				text-transform: uppercase;
				margin-right: auto;
			}
		}

		&-container {
			position: absolute;
			padding-top: 4.7rem; //match with heading height
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			flex-grow: 1;
			background-color: $color-white;
			overflow-y: auto;

			&-list {
				display: flex;
				flex-flow: column nowrap;
				width: 100%;
			}

			&-order {
				display: flex;
				flex-flow: row nowrap;
				align-items: center;
				padding: 1.5rem 0.8rem;
				width: 100%;
				font-size: 1.4rem;

				span {
					margin-right: 0.8rem;
				}

				a {
					color: $color-tertiary;
				}
			}
		}
	}

	&-pricing {
		display: inline-flex;
		flex-flow: column nowrap;
		width: 34%;
		height: 100%;
		text-align: right;
		align-items: flex-end;
		justify-content: flex-end;

		.form-input {
			margin: 0.4rem 0;

			label {
				font-weight: 700;
			}
			.v-text-field {
				min-width: 16rem;
				text-align: right;

				&::v-deep {
					input {
						text-align: right;
						padding-right: 0.8rem;
					}
				}
			}
		}
	}
}

.validation-error {
	color: $color-red;
	font-size: 1.2rem;
}
</style>
